import React, { useEffect, useMemo, useState } from 'react';
import { IonIcon, IonSpinner, IonToast } from '@ionic/react';
import { addOutline, calendarOutline, ribbonOutline, closeOutline } from 'ionicons/icons';
import {
  listStock,
  createStock,
  updateStock,
  deleteStock,
  stockCandidates,
  assignStock,
  unassignStock,
  listStockEvents,
  excludeStockEvent,
  includeStockEvent,
  StockItem,
  StockCandidate,
  StockEventOption,
} from '../services/stockService';

/**
 * Stock screen: ticket lots the user holds and their assignment onto platform orders.
 *  - "Match stock": a lot bought for one specific match; remaining = quantity − assignments.
 *  - "Season ticket": same quantity/category/cost for EVERY home game of a team; per-game remaining.
 * Assigning stamps the order's Bought From + Price Bought (qty × price/ticket); orders that already
 * carry a supplier/cost are blocked server-side (clear them on the Orders screen to reassign).
 */

type EditorState = {
  id?: string; // set = editing
  kind: 'event' | 'season';
  eventName: string;
  team: string;
  quantity: string;
  category: string;
  split: string;
  pricePerTicket: string;
  seasonTotalPrice: string;
  seasonGames: string;
  boughtFrom: string;
  notes: string;
};

const emptyEditor = (kind: 'event' | 'season'): EditorState => ({
  kind,
  eventName: '',
  team: '',
  quantity: '',
  category: '',
  split: '',
  pricePerTicket: '',
  seasonTotalPrice: '',
  seasonGames: '19',
  boughtFrom: '',
  notes: '',
});

const inputCls =
  'w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm';

const StockContent: React.FC = () => {
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);

  // Fixture list (all PL games from market data) for the event autocomplete — loaded once on demand.
  const [events, setEvents] = useState<StockEventOption[] | null>(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const ensureEvents = () => {
    if (events !== null) return;
    listStockEvents()
      .then(setEvents)
      .catch(() => setEvents([]));
  };

  // Distinct home teams derived from the fixture list, for the season-ticket team autocomplete.
  const teams = useMemo(() => {
    if (!events) return [];
    const seen = new Map<string, string>(); // lowercase → display casing
    for (const ev of events) {
      const home = ev.name.split(/\s+vs\.?\s+/i)[0]?.trim();
      if (home && !seen.has(home.toLowerCase())) seen.set(home.toLowerCase(), home);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [events]);

  // Assign dialog state
  const [assignFor, setAssignFor] = useState<StockItem | null>(null);
  const [cands, setCands] = useState<StockCandidate[] | null>(null);
  const [pickedOrder, setPickedOrder] = useState<StockCandidate | null>(null);
  const [assignQty, setAssignQty] = useState('');
  const [assigning, setAssigning] = useState(false);

  const load = async () => {
    try {
      setItems(await listStock());
    } catch {
      setToast('Failed to load stock');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // ── Add / edit ──────────────────────────────────────────────────────────────
  const saveEditor = async () => {
    if (!editor || saving) return;
    setSaving(true);
    try {
      const payload: any = {
        kind: editor.kind,
        eventName: editor.eventName,
        team: editor.team,
        quantity: Number(editor.quantity),
        category: editor.category,
        split: editor.split,
        boughtFrom: editor.boughtFrom,
        notes: editor.notes,
      };
      if (editor.kind === 'season') {
        payload.seasonTotalPrice = Number(editor.seasonTotalPrice);
        payload.seasonGames = Number(editor.seasonGames) || 19;
      } else {
        payload.pricePerTicket = Number(editor.pricePerTicket);
      }
      if (editor.id) await updateStock(editor.id, payload as any);
      else await createStock(payload as any);
      setEditor(null);
      await load();
      setToast('Saved');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (it: StockItem) => {
    ensureEvents(); // fixtures for event autocomplete / teams for season autocomplete
    setEditor({
      id: it._id,
      kind: it.kind,
      eventName: it.eventName || '',
      team: it.team || '',
      quantity: String(it.quantity ?? ''),
      category: it.category || '',
      split: it.split || '',
      pricePerTicket: String(it.pricePerTicket ?? ''),
      seasonTotalPrice: String(it.seasonTotalPrice ?? ''),
      seasonGames: String(it.seasonGames ?? '19'),
      boughtFrom: it.boughtFrom || '',
      notes: it.notes || '',
    });
  };

  const removeItem = async (it: StockItem) => {
    const label = it.kind === 'season' ? `${it.team} season tickets` : it.eventName;
    if (!window.confirm(`Delete "${label}"? Orders already assigned keep their supplier/cost.`)) return;
    try {
      await deleteStock(it._id);
      await load();
      setToast('Deleted');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Delete failed');
    }
  };

  // ── Assign ──────────────────────────────────────────────────────────────────
  const openAssign = async (it: StockItem) => {
    setAssignFor(it);
    setCands(null);
    setPickedOrder(null);
    setAssignQty('');
    try {
      const r = await stockCandidates(it._id);
      setCands(r.candidates);
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to load matching orders');
      setAssignFor(null);
    }
  };

  const pick = (c: StockCandidate) => {
    setPickedOrder(c);
    setAssignQty(String(Math.min(c.quantity || 1, c.remainingForThisGame)));
  };

  const doAssign = async () => {
    if (!assignFor || !pickedOrder || assigning) return;
    const qty = Number(assignQty);
    if (!qty || qty < 1) {
      setToast('Enter a quantity');
      return;
    }
    setAssigning(true);
    try {
      await assignStock(assignFor._id, pickedOrder.orderId, pickedOrder.platform, qty);
      setToast(
        `Assigned ${qty} to order ${pickedOrder.orderId} — Bought From + Price Bought stamped.`,
      );
      setAssignFor(null);
      await load();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Assign failed');
    } finally {
      setAssigning(false);
    }
  };

  // Season items: mark a fixture as not covered (friendlies etc.) / restore it.
  const removeGame = async (it: StockItem, eventName: string) => {
    try {
      await excludeStockEvent(it._id, eventName);
      await load();
      setToast(`Removed "${eventName}" from this season ticket`);
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed');
    }
  };
  const restoreGame = async (it: StockItem, eventName: string) => {
    try {
      await includeStockEvent(it._id, eventName);
      await load();
      setToast('Game restored');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed');
    }
  };

  const undoAssignment = async (it: StockItem, orderId: string, platform: string) => {
    if (!window.confirm(`Un-assign order ${orderId}? Its supplier/cost will be cleared.`)) return;
    try {
      await unassignStock(it._id, orderId, platform);
      await load();
      setToast('Assignment removed');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-3 max-w-5xl mx-auto">
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => {
            ensureEvents();
            setEditor(emptyEditor('event'));
          }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white"
        >
          <IonIcon icon={addOutline} /> Add match stock
        </button>
        <button
          onClick={() => {
            ensureEvents();
            setEditor(emptyEditor('season'));
          }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-blue-600 text-blue-600 dark:text-blue-400"
        >
          <IonIcon icon={ribbonOutline} /> Add season ticket
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <IonSpinner name="crescent" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No stock yet. Add a lot you bought for a match, or a season ticket.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it._id}
              className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        'text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide ' +
                        (it.kind === 'season'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300')
                      }
                    >
                      {it.kind === 'season' ? 'Season' : 'Match'}
                    </span>
                    <span className="font-medium truncate">
                      {it.kind === 'season' ? `${it.team} — all home games` : it.eventName}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {[
                      it.category,
                      it.split,
                      it.kind === 'season'
                        ? (it.seasonTotalPrice != null
                            ? `£${Number(it.seasonTotalPrice).toFixed(2)}/season → `
                            : '') + `£${Number(it.pricePerTicket).toFixed(2)}/game`
                        : `£${Number(it.pricePerTicket).toFixed(2)}/ticket`,
                      it.boughtFrom ? `from ${it.boughtFrom}` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {it.notes ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{it.notes}</div>
                  ) : null}
                </div>
                <div className="text-right shrink-0">
                  {it.kind === 'season' ? (
                    <div className="text-sm font-semibold">{it.quantity} / game</div>
                  ) : (
                    <div
                      className={
                        'text-sm font-semibold ' +
                        ((it.remaining ?? 0) === 0 ? 'text-gray-400' : 'text-green-600 dark:text-green-400')
                      }
                    >
                      {it.remaining} of {it.quantity} left
                    </div>
                  )}
                  <div className="flex gap-1.5 mt-1.5 justify-end">
                    <button
                      onClick={() => openAssign(it)}
                      disabled={it.kind === 'event' && (it.remaining ?? 0) === 0}
                      className="px-2 py-1 rounded-lg text-xs bg-green-600 text-white disabled:opacity-40"
                    >
                      Assign
                    </button>
                    <button
                      onClick={() => startEdit(it)}
                      className="px-2 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => removeItem(it)}
                      className="px-2 py-1 rounded-lg text-xs border border-red-300 text-red-500"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Season: the team's home fixtures with per-game usage */}
              {it.kind === 'season' && (it.usage?.length ?? 0) > 0 && (
                <div className="mt-2 max-h-44 overflow-y-auto rounded border border-gray-100 dark:border-gray-700/60 divide-y divide-gray-100 dark:divide-gray-700/40">
                  {it.usage!.map((u) => (
                    <div
                      key={`${u.eventName}-${u.date}`}
                      className="flex items-center justify-between gap-2 px-2 py-1 text-xs"
                    >
                      <span className="flex items-center gap-1 min-w-0">
                        <IonIcon icon={calendarOutline} className="text-gray-400 shrink-0" />
                        <span className="truncate text-gray-600 dark:text-gray-300">
                          {u.date ? `${u.date} · ` : ''}
                          {u.eventName}
                        </span>
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        <span
                          className={
                            u.remaining === 0
                              ? 'text-gray-400'
                              : u.used > 0
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-green-600 dark:text-green-400'
                          }
                        >
                          {u.used > 0 ? `${u.used} used · ` : ''}
                          {u.remaining} left
                        </span>
                        {u.used === 0 && (
                          <button
                            onClick={() => removeGame(it, u.eventName)}
                            title="Not covered by this season ticket (e.g. friendly) — remove"
                            className="p-0.5 text-gray-300 hover:text-red-500"
                          >
                            <IonIcon icon={closeOutline} />
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Season: excluded games (restorable) */}
              {it.kind === 'season' && (it.excludedList?.length ?? 0) > 0 && (
                <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                  Not in season ticket:{' '}
                  {it.excludedList!.map((x, i) => (
                    <span key={x.eventName}>
                      {i > 0 ? ' · ' : ''}
                      {x.eventName}
                      <button
                        onClick={() => restoreGame(it, x.eventName)}
                        className="ml-0.5 underline hover:text-blue-500"
                        title="Restore this game"
                      >
                        restore
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Assignments */}
              {(it.assignments?.length ?? 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/60 space-y-0.5">
                  {it.assignments.map((a) => (
                    <div
                      key={`${a.orderId}-${a.at}`}
                      className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300"
                    >
                      <span className="truncate">
                        → order {a.orderId} ({a.platform}) · {a.qty} tix · £{a.priceTotal.toFixed(2)}
                        {it.kind === 'season' && a.eventName ? ` · ${a.eventName}` : ''}
                      </span>
                      <button
                        onClick={() => undoAssignment(it, a.orderId, a.platform)}
                        title="Un-assign (clears the order's supplier/cost)"
                        className="p-0.5 text-gray-400 hover:text-red-500"
                      >
                        <IonIcon icon={closeOutline} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add / edit modal */}
      {editor && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 p-4 space-y-2.5">
            <div className="font-medium">
              {editor.id ? 'Edit' : 'Add'}{' '}
              {editor.kind === 'season' ? 'season ticket' : 'match stock'}
            </div>
            {editor.kind === 'event' ? (
              <div className="relative">
                <div className="text-xs text-gray-500 mb-0.5">
                  Match / event name — pick a fixture or type a custom event
                </div>
                <input
                  className={inputCls}
                  value={editor.eventName}
                  onChange={(e) => {
                    setEditor({ ...editor, eventName: e.target.value });
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                  placeholder="Arsenal vs Chelsea"
                />
                {showSuggest && events && editor.eventName.trim() && (
                  <div className="absolute z-50 left-0 right-0 mt-0.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto">
                    {events
                      .filter((ev) => {
                        const words = editor.eventName.toLowerCase().split(/\s+/).filter(Boolean);
                        const name = ev.name.toLowerCase();
                        return words.every((w) => name.includes(w));
                      })
                      .slice(0, 8)
                      .map((ev) => (
                        <button
                          key={`${ev.name}-${ev.date}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditor({ ...editor, eventName: ev.name });
                            setShowSuggest(false);
                          }}
                          className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between gap-2"
                        >
                          <span className="truncate">{ev.name}</span>
                          {ev.date && <span className="text-xs text-gray-400 shrink-0">{ev.date}</span>}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                <div className="text-xs text-gray-500 mb-0.5">
                  Home team (applies to every &quot;&lt;team&gt; vs …&quot; game)
                </div>
                <input
                  className={inputCls}
                  value={editor.team}
                  onChange={(e) => {
                    setEditor({ ...editor, team: e.target.value });
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                  placeholder="Arsenal"
                />
                {showSuggest && teams.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-0.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto">
                    {teams
                      .filter((t) =>
                        editor.team.trim()
                          ? t.toLowerCase().includes(editor.team.trim().toLowerCase())
                          : true,
                      )
                      .slice(0, 10)
                      .map((t) => (
                        <button
                          key={t}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditor({ ...editor, team: t });
                            setShowSuggest(false);
                          }}
                          className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {t}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-gray-500 mb-0.5">
                  Quantity{editor.kind === 'season' ? ' (per game)' : ''}
                </div>
                <input
                  className={inputCls}
                  type="number"
                  min={1}
                  value={editor.quantity}
                  onChange={(e) => setEditor({ ...editor, quantity: e.target.value })}
                />
              </div>
              {editor.kind === 'event' ? (
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">£ / ticket</div>
                  <input
                    className={inputCls}
                    type="number"
                    min={0}
                    step="0.01"
                    value={editor.pricePerTicket}
                    onChange={(e) => setEditor({ ...editor, pricePerTicket: e.target.value })}
                  />
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Season price / seat (£)</div>
                    <input
                      className={inputCls}
                      type="number"
                      min={0}
                      step="0.01"
                      value={editor.seasonTotalPrice}
                      onChange={(e) => setEditor({ ...editor, seasonTotalPrice: e.target.value })}
                      placeholder="950"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Home games</div>
                    <input
                      className={inputCls}
                      type="number"
                      min={1}
                      value={editor.seasonGames}
                      onChange={(e) => setEditor({ ...editor, seasonGames: e.target.value })}
                    />
                  </div>
                  {Number(editor.seasonTotalPrice) > 0 && Number(editor.seasonGames) > 0 && (
                    <div className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                      = £
                      {(Number(editor.seasonTotalPrice) / Number(editor.seasonGames)).toFixed(2)}
                      /seat per game (stamped on assigned orders)
                    </div>
                  )}
                </>
              )}
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Category</div>
                <input
                  className={inputCls}
                  value={editor.category}
                  onChange={(e) => setEditor({ ...editor, category: e.target.value })}
                  placeholder="Shortside Upper"
                />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Split</div>
                <input
                  className={inputCls}
                  value={editor.split}
                  onChange={(e) => setEditor({ ...editor, split: e.target.value })}
                  placeholder="all together / pairs…"
                />
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Bought from</div>
              <input
                className={inputCls}
                value={editor.boughtFrom}
                onChange={(e) => setEditor({ ...editor, boughtFrom: e.target.value })}
                placeholder="supplier / source"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Notes</div>
              <input
                className={inputCls}
                value={editor.notes}
                onChange={(e) => setEditor({ ...editor, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setEditor(null)}
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={saveEditor}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 p-4">
            <div className="font-medium mb-1">
              Assign{' '}
              {assignFor.kind === 'season'
                ? `${assignFor.team} season tickets`
                : assignFor.eventName}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Orders below match this stock and have no supplier/cost yet. Assigning stamps Bought
              From + Price Bought (£{Number(assignFor.pricePerTicket).toFixed(2)}/ticket).
            </div>
            {cands === null ? (
              <div className="flex justify-center py-6">
                <IonSpinner name="crescent" />
              </div>
            ) : cands.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No matching unassigned orders found.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1">
                {cands.map((c) => (
                  <button
                    key={`${c.platform}-${c.orderId}`}
                    onClick={() => pick(c)}
                    className={
                      'w-full text-left px-2.5 py-1.5 rounded-lg border text-sm ' +
                      (pickedOrder?.orderId === c.orderId && pickedOrder?.platform === c.platform
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700')
                    }
                  >
                    <div className="flex justify-between gap-2">
                      <span className="font-medium truncate">
                        {c.orderId} · {c.platform}
                      </span>
                      <span className="text-xs text-gray-500 shrink-0">
                        {c.quantity} tix{c.eventDate ? ` · ${c.eventDate}` : ''}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {[c.eventName, c.buyerName].filter(Boolean).join(' · ')}
                      {' — '}
                      {c.remainingForThisGame} in stock
                      {assignFor.kind === 'season' ? ' for this game' : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 mt-3">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-xs text-gray-500">Qty</span>
                <input
                  className="w-16 px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
                  type="number"
                  min={1}
                  value={assignQty}
                  onChange={(e) => setAssignQty(e.target.value)}
                  disabled={!pickedOrder}
                />
                {pickedOrder && (
                  <span className="text-xs text-gray-400">
                    = £{((Number(assignQty) || 0) * Number(assignFor.pricePerTicket)).toFixed(2)}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setAssignFor(null)}
                  className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={doAssign}
                  disabled={!pickedOrder || assigning}
                  className="px-3 py-1.5 rounded-lg text-sm bg-green-600 text-white disabled:opacity-50"
                >
                  {assigning ? 'Assigning…' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <IonToast isOpen={!!toast} message={toast} duration={3500} onDidDismiss={() => setToast('')} />
    </div>
  );
};

export default StockContent;
